export type DeliveryStatusValue =
  | "no_survey_scheduled"
  | "survey_scheduled"
  | "in_progress"
  | "delivered";

export type DeliveryState = {
  survey_completed: boolean;
  report_completed: boolean;
  vr_tours_completed: boolean;
  files_documents_completed: boolean;
  delivery_status: DeliveryStatusValue;
};

export type DeliveryFlagsInput = {
  hasSiteVisit: boolean;
  hasSiteReport: boolean;
  vrToursCompleted: boolean;
  filesDocumentsCompleted: boolean;
  hasScanDate: boolean;
};

/**
 * `documents.category` values that count as an actual site report.
 * Intentionally STRICTER than the Site Reports tab in usePropertyAllFiles —
 * `photos` is excluded (per the 2026-06-24 design review).
 */
export const SITE_REPORT_DOCUMENT_CATEGORIES = [
  "site reports",
  "site_report",
  "site_summary",
  "category_summary",
] as const;

// Object keys (not a bare array) so adding/removing a field on DeliveryState
// forces this list to update — the `satisfies` check fails on a missing key.
const DELIVERY_STATE_KEYS = Object.keys({
  survey_completed: true,
  report_completed: true,
  vr_tours_completed: true,
  files_documents_completed: true,
  delivery_status: true,
} satisfies Record<keyof DeliveryState, boolean>) as (keyof DeliveryState)[];

export function deriveDeliveryState(input: DeliveryFlagsInput): DeliveryState {
  const flags = [
    input.hasSiteVisit,
    input.hasSiteReport,
    input.vrToursCompleted,
    input.filesDocumentsCompleted,
  ];
  const delivery_status: DeliveryStatusValue = flags.every(Boolean)
    ? "delivered"
    : flags.some(Boolean)
    ? "in_progress"
    : input.hasScanDate
    ? "survey_scheduled"
    : "no_survey_scheduled";

  return {
    survey_completed: input.hasSiteVisit,
    report_completed: input.hasSiteReport,
    vr_tours_completed: input.vrToursCompleted,
    files_documents_completed: input.filesDocumentsCompleted,
    delivery_status,
  };
}

export type PropertyPlan = {
  id: string;
  name: string;
  current: DeliveryState;
  next: DeliveryState;
  changed: boolean;
  changedColumns: (keyof DeliveryState)[];
};

export function diffDeliveryState(
  current: DeliveryState,
  next: DeliveryState
): { changed: boolean; changedColumns: (keyof DeliveryState)[] } {
  const changedColumns = DELIVERY_STATE_KEYS.filter((k) => current[k] !== next[k]);
  return { changed: changedColumns.length > 0, changedColumns };
}

const PROPERTY_SELECT =
  "id, name, address, survey_completed, report_completed, vr_tours_completed, files_documents_completed, delivery_status, scan_date";

function toDeliveryState(row: Record<string, unknown>): DeliveryState {
  return {
    survey_completed: row.survey_completed === true,
    report_completed: row.report_completed === true,
    vr_tours_completed: row.vr_tours_completed === true,
    files_documents_completed: row.files_documents_completed === true,
    delivery_status: (row.delivery_status ?? "survey_scheduled") as DeliveryStatusValue,
  };
}

function planFromRow(
  row: Record<string, unknown>,
  hasSiteVisit: boolean,
  hasSiteReport: boolean
): PropertyPlan {
  const current = toDeliveryState(row);
  const next = deriveDeliveryState({
    hasSiteVisit,
    hasSiteReport,
    vrToursCompleted: current.vr_tours_completed,
    filesDocumentsCompleted: current.files_documents_completed,
    hasScanDate: row.scan_date != null,
  });
  const { changed, changedColumns } = diffDeliveryState(current, next);
  return {
    id: String(row.id),
    name: String(row.name || row.address || "Unnamed"),
    current,
    next,
    changed,
    changedColumns,
  };
}

/**
 * Lazily imports the Supabase client so this module's pure exports
 * (deriveDeliveryState / diffDeliveryState) stay importable in unit tests
 * without pulling in the real supabase/firebase chain.
 */
async function getDb(): Promise<any> {
  const { supabase } = await import("@/shared/integrations/supabase/client");
  return supabase as unknown as any;
}

export async function buildPropertyPlan(propertyId: string): Promise<PropertyPlan> {
  const db = await getDb();

  const { data: row, error } = await db
    .from("properties")
    .select(PROPERTY_SELECT)
    .eq("id", propertyId)
    .single();
  if (error) throw error;

  const [visit, report] = await Promise.all([
    db
      .from("property_attributes")
      .select("id", { count: "exact", head: true })
      .eq("property_id", propertyId),
    db
      .from("documents")
      .select("id", { count: "exact", head: true })
      .eq("property_id", propertyId)
      .in("category", SITE_REPORT_DOCUMENT_CATEGORIES as unknown as string[]),
  ]);
  if (visit.error) throw visit.error;
  if (report.error) throw report.error;

  return planFromRow(row, (visit.count ?? 0) > 0, (report.count ?? 0) > 0);
}

/** PostgREST caps a single response at max_rows; page past it so large reads aren't silently truncated. */
const SUPABASE_PAGE_SIZE = 1000;
/** Cap on `.in()` filter size: every id is serialized into the GET URL, so keep lists short enough to stay under URL limits. */
const ID_FILTER_CHUNK = 300;

/** Splits an array into chunks of at most `size`. */
function chunkIds(ids: string[], size: number): string[][] {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += size) chunks.push(ids.slice(i, i + size));
  return chunks;
}

/**
 * Runs a range-paginated query, looping until a page returns fewer than a full
 * page of rows. The query MUST impose a stable total order (e.g. `.order("id")`)
 * or paging can skip/duplicate rows across requests.
 */
async function fetchAllRows<T>(
  page: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: unknown }>
): Promise<T[]> {
  const all: T[] = [];
  for (let from = 0; ; from += SUPABASE_PAGE_SIZE) {
    const { data, error } = await page(from, from + SUPABASE_PAGE_SIZE - 1);
    if (error) throw error;
    const rows = data ?? [];
    all.push(...rows);
    if (rows.length < SUPABASE_PAGE_SIZE) break;
  }
  return all;
}

/**
 * Collects the set of property ids that have ≥1 row in `table`. The id filter is
 * chunked (URL-length safety) and each chunk is range-paginated with a stable
 * order. Only `property_id` is read; the `Set` dedups across pages and chunks.
 */
async function propertyIdsWithRows(
  db: any,
  table: string,
  ids: string[],
  refine?: (query: any) => any
): Promise<Set<string>> {
  const found = new Set<string>();
  for (const idChunk of chunkIds(ids, ID_FILTER_CHUNK)) {
    const rows = await fetchAllRows<{ property_id: string }>((from, to) => {
      const base = db.from(table).select("property_id").in("property_id", idChunk);
      return (refine ? refine(base) : base).order("id").range(from, to);
    });
    for (const r of rows) found.add(r.property_id);
  }
  return found;
}

/**
 * Builds plans for an explicit set of property ids — the bulk caller passes the
 * exact properties it is displaying, so the recompute set always matches the UI
 * regardless of how `useProjects` groups properties (org vs. project). The id
 * filters are chunked and every read is range-paginated with a stable order, so
 * large projects neither overflow the request URL nor silently truncate rows.
 */
export async function buildPropertyPlans(propertyIds: string[]): Promise<PropertyPlan[]> {
  if (propertyIds.length === 0) return [];
  const db = await getDb();

  const properties: Record<string, unknown>[] = [];
  for (const idChunk of chunkIds(propertyIds, ID_FILTER_CHUNK)) {
    const rows = await fetchAllRows<Record<string, unknown>>((from, to) =>
      db.from("properties").select(PROPERTY_SELECT).in("id", idChunk).order("id").range(from, to)
    );
    properties.push(...rows);
  }
  if (properties.length === 0) return [];
  const ids = properties.map((p) => String(p.id));

  const [withVisit, withReport] = await Promise.all([
    propertyIdsWithRows(db, "property_attributes", ids),
    propertyIdsWithRows(db, "documents", ids, (q) =>
      q.in("category", SITE_REPORT_DOCUMENT_CATEGORIES as unknown as string[])
    ),
  ]);

  return properties.map((row) =>
    planFromRow(row, withVisit.has(String(row.id)), withReport.has(String(row.id)))
  );
}

/**
 * Writes only the three derived columns for plans whose state changed.
 * vr_tours_completed / files_documents_completed are preserved (never written).
 * Failures are counted, never swallowed.
 */
export async function applyPlans(
  plans: PropertyPlan[]
): Promise<{ updated: number; failed: number }> {
  const db = await getDb();

  const changed = plans.filter((p) => p.changed);
  let updated = 0;
  let failed = 0;
  for (const plan of changed) {
    const { error } = await db
      .from("properties")
      .update({
        survey_completed: plan.next.survey_completed,
        report_completed: plan.next.report_completed,
        delivery_status: plan.next.delivery_status,
      })
      .eq("id", plan.id);
    if (error) {
      console.error("[applyPlans] update failed for property", plan.id, error);
      failed += 1;
    } else {
      updated += 1;
    }
  }
  return { updated, failed };
}
